import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { PLOT_COUNT, PLOT_SIZE } from '../game/constants';
import { FLOWERS, type FlowerId } from '../game/flowers';
import { plotPosition } from '../game/grid';
import type { IntentTone } from '../game/interaction';
import type { GameState } from '../game/state';
import { damp } from '../utils/math';
import { flowerGeometry, growthStage, stageProgress, type Stage } from './flowerModels';
import { GeometryBuilder, Templates as T, trs } from './GeometryBuilder';
import { addWind, toonUnique } from './materials';

export interface PlotPick {
  kind: 'plot';
  plotId: number;
}

const SOIL_TOP = 0.26;
const DRY = new THREE.Color('#c08a5c');
const WET = new THREE.Color('#7d5134');

const TONE_COLORS: Record<IntentTone, string> = {
  harvest: '#ffd23f',
  plant: '#8ee06b',
  water: '#5fb4ff',
  buy: '#ffb347',
  dig: '#ff9b73',
  info: '#ffffff',
  blocked: '#ff6b6b',
};

function soilGeometry(): THREE.BufferGeometry {
  const b = new GeometryBuilder();
  const box = new RoundedBoxGeometry(PLOT_SIZE - 0.1, 0.3, PLOT_SIZE - 0.1, 2, 0.09);
  b.add(box, '#ffffff', trs([0, 0.1, 0]));
  // Furrow ridges, slightly darker (the material colour tints all of it).
  for (let i = -1; i <= 1; i++) {
    b.add(
      T.sphereLow,
      '#d9cbbd',
      trs([0, SOIL_TOP - 0.01, i * 0.5], [0, 0, 0], [0.82, 0.05, 0.13]),
    );
  }
  box.dispose();
  return b.build().base;
}

function roundedRectRing(size: number, thickness: number, radius: number): THREE.ShapeGeometry {
  const rect = (s: number, r: number) => {
    const shape = new THREE.Shape();
    const h = s / 2;
    shape.moveTo(-h + r, -h);
    shape.lineTo(h - r, -h);
    shape.quadraticCurveTo(h, -h, h, -h + r);
    shape.lineTo(h, h - r);
    shape.quadraticCurveTo(h, h, h - r, h);
    shape.lineTo(-h + r, h);
    shape.quadraticCurveTo(-h, h, -h, h - r);
    shape.lineTo(-h, -h + r);
    shape.quadraticCurveTo(-h, -h, -h + r, -h);
    return shape;
  };
  const outer = rect(size, radius);
  outer.holes.push(rect(size - thickness * 2, Math.max(0.01, radius - thickness)));
  const geometry = new THREE.ShapeGeometry(outer, 6);
  geometry.rotateX(-Math.PI / 2);
  return geometry;
}

function badgeTexture(emoji: string): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 96;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.beginPath();
    ctx.arc(48, 44, 34, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(36, 72);
    ctx.lineTo(60, 72);
    ctx.lineTo(48, 90);
    ctx.fill();
    ctx.font = '40px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(emoji, 48, 47);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

interface PlotVisual {
  group: THREE.Group;
  soil: THREE.Mesh;
  soilMaterial: THREE.MeshToonMaterial;
  lockedMarker: THREE.Mesh;
  flower: THREE.Mesh;
  glow: THREE.Mesh;
  thirsty: THREE.Sprite;
  key: string;
  stage: Stage | -1;
  pop: number;
  unlocked: boolean | null;
  unlockPop: number;
  golden: boolean;
}

/** Renders the 6×6 field and keeps it in sync with the game state. */
export class FarmPlots {
  readonly group = new THREE.Group();
  readonly pickables: THREE.Object3D[] = [];

  private readonly visuals: PlotVisual[] = [];
  private readonly cursor: THREE.Mesh<THREE.ShapeGeometry, THREE.MeshBasicMaterial>;
  private readonly flowerMaterial: THREE.MeshToonMaterial;
  private readonly goldenMaterial: THREE.MeshToonMaterial;
  private readonly glowMaterial: THREE.MeshBasicMaterial;
  private hovered: number | null = null;

  constructor(private readonly castShadows: boolean) {
    this.flowerMaterial = addWind(toonUnique({ vertexColors: true }), 0.09, 'flower');
    this.goldenMaterial = addWind(
      toonUnique({
        vertexColors: true,
        color: '#ffe9a0',
        emissive: '#ffb400',
        emissiveIntensity: 0.35,
      }),
      0.09,
      'flower-golden',
    );
    this.glowMaterial = addWind(
      new THREE.MeshBasicMaterial({ vertexColors: true }),
      0.09,
      'flower-glow',
    );

    const soil = soilGeometry();
    // Kept low (soil height): tall boxes would steal clicks from the plot behind
    // them at the camera's viewing angle.
    const pick = new THREE.BoxGeometry(PLOT_SIZE + 0.35, 0.5, PLOT_SIZE + 0.35);
    const pickMaterial = new THREE.MeshBasicMaterial();
    const lockedGeometry = roundedRectRing(PLOT_SIZE - 0.15, 0.08, 0.25);
    const lockedMaterial = new THREE.MeshBasicMaterial({
      color: '#ffffff',
      transparent: true,
      opacity: 0.45,
      depthWrite: false,
    });
    const thirstyTexture = badgeTexture('💧');

    for (let id = 0; id < PLOT_COUNT; id++) {
      const { x, z } = plotPosition(id);
      const group = new THREE.Group();
      group.position.set(x, 0, z);

      const soilMaterial = toonUnique({ color: DRY.clone() });
      const soilMesh = new THREE.Mesh(soil, soilMaterial);
      soilMesh.receiveShadow = true;
      group.add(soilMesh);

      const lockedMarker = new THREE.Mesh(lockedGeometry, lockedMaterial);
      lockedMarker.position.y = 0.04;
      group.add(lockedMarker);

      const flower = new THREE.Mesh(new THREE.BufferGeometry(), this.flowerMaterial);
      flower.position.y = SOIL_TOP - 0.02;
      flower.castShadow = castShadows;
      flower.visible = false;
      group.add(flower);

      const glow = new THREE.Mesh(new THREE.BufferGeometry(), this.glowMaterial);
      glow.position.y = SOIL_TOP - 0.02;
      glow.visible = false;
      group.add(glow);

      const thirsty = new THREE.Sprite(
        new THREE.SpriteMaterial({ map: thirstyTexture, depthWrite: false }),
      );
      thirsty.scale.setScalar(0.62);
      thirsty.position.y = 1.4;
      thirsty.visible = false;
      group.add(thirsty);

      const pickMesh = new THREE.Mesh(pick, pickMaterial);
      pickMesh.visible = false;
      pickMesh.position.y = 0.25;
      pickMesh.userData.pick = { kind: 'plot', plotId: id } satisfies PlotPick;
      group.add(pickMesh);
      this.pickables.push(pickMesh);

      this.group.add(group);
      this.visuals.push({
        group,
        soil: soilMesh,
        soilMaterial,
        lockedMarker,
        flower,
        glow,
        thirsty,
        key: '',
        stage: -1,
        pop: 0,
        unlocked: null,
        unlockPop: 0,
        golden: false,
      });
    }

    this.cursor = new THREE.Mesh(
      roundedRectRing(PLOT_SIZE + 0.2, 0.12, 0.32),
      new THREE.MeshBasicMaterial({
        color: '#ffffff',
        transparent: true,
        opacity: 0.9,
        depthWrite: false,
      }),
    );
    this.cursor.visible = false;
    this.cursor.renderOrder = 2;
    this.group.add(this.cursor);
  }

  setHover(plotId: number | null, tone: IntentTone = 'info'): void {
    this.hovered = plotId;
    this.cursor.material.color.set(TONE_COLORS[tone]);
  }

  /** Brief bounce on a plot (used for action feedback). */
  bump(plotId: number, amount = 0.6): void {
    const visual = this.visuals[plotId];
    if (visual) visual.pop = Math.max(visual.pop, amount);
  }

  /** Position just above a plot's soil, for effects. */
  plotTop(plotId: number, out = new THREE.Vector3()): THREE.Vector3 {
    const { x, z } = plotPosition(plotId);
    return out.set(x, SOIL_TOP + 0.2, z);
  }

  goldenPlots(): number[] {
    return this.visuals.flatMap((v, i) => (v.golden ? [i] : []));
  }

  update(dt: number, time: number, state: GameState, night: number): void {
    // Unlit glow parts read as softly luminous, twinkling a little at night.
    this.glowMaterial.color.setScalar(1 + night * 0.25 * (0.5 + 0.5 * Math.sin(time * 2.2)));

    state.plots.forEach((plot, id) => {
      const v = this.visuals[id];
      if (!v) return;

      if (v.unlocked !== plot.unlocked) {
        if (v.unlocked === false && plot.unlocked) v.unlockPop = 1;
        v.unlocked = plot.unlocked;
        v.soil.visible = plot.unlocked;
        v.lockedMarker.visible = !plot.unlocked;
      }
      if (v.unlockPop > 0) {
        v.unlockPop = Math.max(0, v.unlockPop - dt * 2.2);
        const t = 1 - v.unlockPop;
        v.soil.scale.set(1, Math.max(0.05, t + Math.sin(t * Math.PI) * 0.25), 1);
      }

      // Soil darkens with moisture.
      v.soilMaterial.color.copy(DRY).lerp(WET, plot.unlocked ? plot.moisture : 0);

      const crop = plot.crop;
      const stage = crop ? growthStage(crop.growth) : -1;
      const golden = !!crop?.golden && stage === 3;
      const key = crop ? `${crop.species}:${stage}` : '';
      if (key !== v.key) {
        if (crop && stage !== -1) this.applyGeometry(v, crop.species, stage);
        else {
          v.flower.visible = false;
          v.glow.visible = false;
        }
        if (stage > v.stage && crop) v.pop = 1;
        v.key = key;
        v.stage = stage;
      }
      if (golden !== v.golden) {
        v.golden = golden;
        v.flower.material = golden ? this.goldenMaterial : this.flowerMaterial;
      }

      if (crop) {
        const within = stageProgress(crop.growth);
        const base = stage === 3 ? 1 : 0.62 + within * 0.38;
        v.pop = Math.max(0, v.pop - dt * 2.5);
        const bounce = Math.sin(v.pop * Math.PI * 2) * v.pop * 0.35;
        const sx = base * (1 + bounce * 0.5);
        const sy = base * (1 - bounce * 0.4 + v.pop * 0.15);
        v.flower.scale.set(sx, sy, sx);
        v.glow.scale.copy(v.flower.scale);
      }

      const thirsty = !!crop && crop.growth < 1 && plot.moisture <= 0 && !state.weather.raining;
      v.thirsty.visible = thirsty;
      if (thirsty) v.thirsty.position.y = 1.35 + Math.sin(time * 3 + id) * 0.08;
    });

    if (this.hovered !== null) {
      const { x, z } = plotPosition(this.hovered);
      this.cursor.visible = true;
      const k = damp(20, dt);
      this.cursor.position.x += (x - this.cursor.position.x) * k;
      this.cursor.position.z += (z - this.cursor.position.z) * k;
      this.cursor.position.y = 0.3;
      const pulse = 1 + Math.sin(time * 6) * 0.03;
      this.cursor.scale.set(pulse, 1, pulse);
    } else {
      this.cursor.visible = false;
    }
  }

  private applyGeometry(v: PlotVisual, species: FlowerId, stage: Stage): void {
    const geometry = flowerGeometry(species, stage);
    v.flower.geometry = geometry.base;
    v.flower.visible = true;
    const glows = stage === 3 && geometry.glow !== null && FLOWERS[species].glow !== undefined;
    v.glow.visible = glows;
    if (geometry.glow) v.glow.geometry = geometry.glow;
    v.flower.castShadow = this.castShadows && stage >= 2;
  }
}
